import { Infinity } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { formatDisplayCredits } from "src/lib/llmCredits";
import { cn } from "src/lib/utils";

export const AssistantCreditsIndicator = ({ creditStatus }) => {
  const weeklyTokenLimit = Math.max(
    0,
    Number(creditStatus?.weeklyTokenLimit || 0)
  );
  const weeklyTokensUsed = Math.max(
    0,
    Number(creditStatus?.weeklyTokensUsed || 0)
  );
  const paymentMethodCreditLimit = Math.max(
    0,
    Number(creditStatus?.paymentMethodCreditLimit || 0)
  );
  const totalTokensRemaining = Math.max(
    0,
    Number(creditStatus?.totalTokensRemaining || 0)
  );
  const totalCredits = Math.max(1, weeklyTokensUsed + totalTokensRemaining);
  const creditsUsed = Math.max(0, totalCredits - totalTokensRemaining);
  const creditsUsedPercent = Math.max(
    0,
    Math.min(100, Math.round((creditsUsed / totalCredits) * 100))
  );
  const isLocked = Boolean(creditStatus?.locked);
  const isWarning = Boolean(creditStatus?.warningThresholdReached);
  const paymentMethodRequired = Boolean(creditStatus?.paymentMethodRequired);
  const unlimited = Boolean(creditStatus?.unlimited);
  const ringRadius = 8;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - creditsUsedPercent / 100);
  const millisUntilReset = creditStatus?.weekEndsAt
    ? new Date(creditStatus.weekEndsAt).getTime() - Date.now()
    : null;
  const daysUntilReset =
    millisUntilReset === null
      ? null
      : Math.max(0, Math.ceil(millisUntilReset / (24 * 60 * 60 * 1000)));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="bg-muted/50 inline-flex size-8 cursor-default items-center justify-center rounded-full"
          aria-label={unlimited ? "Unlimited AI - fair use" : "View AI credits"}
        >
          {unlimited ? (
            <Infinity className="text-primary size-4" />
          ) : (
            <svg viewBox="0 0 24 24" className="size-5 -rotate-90">
              <circle
                cx="12"
                cy="12"
                r={ringRadius}
                className="stroke-muted-foreground/25 fill-none"
                strokeWidth="3"
              />
              <circle
                cx="12"
                cy="12"
                r={ringRadius}
                className={cn(
                  "fill-none transition-[stroke-dashoffset] duration-200",
                  isLocked || isWarning
                    ? "stroke-destructive"
                    : "stroke-foreground/85"
                )}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${ringCircumference} ${ringCircumference}`}
                strokeDashoffset={ringOffset}
              />
            </svg>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-72 max-w-none rounded-xl p-3 text-left"
      >
        <div className="space-y-2">
          {unlimited ? (
            <>
              <div className="text-sm font-medium">Unlimited - fair use</div>
              <div className="text-muted-foreground text-xs leading-5">
                Included with Lucris Advisory for this store. Interactive team
                use remains subject to the fair-use policy.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">
                {formatDisplayCredits(totalTokensRemaining)} credits left
              </div>
              <div className="text-muted-foreground text-xs">
                {creditStatus
                  ? `Usage: ${creditsUsedPercent}% (${formatDisplayCredits(creditsUsed)} / ${formatDisplayCredits(totalCredits)})`
                  : "Loading credit usage..."}
              </div>
              <div className="text-muted-foreground whitespace-nowrap text-xs">
                {creditStatus
                  ? `Weekly allowance: ${formatDisplayCredits(weeklyTokenLimit)} (resets in ${daysUntilReset ?? 0}d)`
                  : ""}
              </div>
              {paymentMethodRequired ? (
                <div className="border-border/70 text-muted-foreground border-t pt-2 text-xs leading-5">
                  No valid payment method is on file. AI credits are capped at{" "}
                  {formatDisplayCredits(paymentMethodCreditLimit)} per week
                  until one is added.
                </div>
              ) : null}
            </>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
